import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { ADMIN_BASE_URL } from "../config/config";
import "../components/App.css";

const DEPARTMENTS = [
  "Technology & Product",
  "Operations & Customer Success",
  "Growth & Business",
  "Support Functions",
];

const emptyOpportunity = {
  title: "",
  department: DEPARTMENTS[0],
  tag: "",
  meta: "",
  description: "",
  isActive: true,
  sortOrder: 0,
};

const STATUS_LABELS = {
  pending: "Pending",
  reviewed: "Reviewed",
  shortlisted: "Shortlisted",
  rejected: "Rejected",
};

const AdminCareers = () => {
  const [activeTab, setActiveTab] = useState("opportunities");
  const [opportunities, setOpportunities] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [pendingStatus, setPendingStatus] = useState("");
  const [form, setForm] = useState(emptyOpportunity);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("care_admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchOpportunities = async () => {
    const response = await axios.get(`${ADMIN_BASE_URL}/careers/opportunities`, {
      headers: getAuthHeaders(),
    });
    setOpportunities(response.data || []);
  };

  const fetchApplications = async () => {
    const response = await axios.get(`${ADMIN_BASE_URL}/careers/applications`, {
      headers: getAuthHeaders(),
    });
    setApplications(response.data || []);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchOpportunities(), fetchApplications()]);
    } catch (error) {
      toast.error("Failed to load careers data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyOpportunity);
    setShowModal(true);
  };

  const openEdit = (opportunity) => {
    setEditingId(opportunity._id);
    setForm({
      title: opportunity.title,
      department: opportunity.department,
      tag: opportunity.tag || "",
      meta: opportunity.meta || "",
      description: opportunity.description || "",
      isActive: opportunity.isActive !== false,
      sortOrder: opportunity.sortOrder || 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${ADMIN_BASE_URL}/careers/opportunities/${editingId}`, form, {
          headers: getAuthHeaders(),
        });
        toast.success("Opportunity updated");
      } else {
        await axios.post(`${ADMIN_BASE_URL}/careers/opportunities`, form, {
          headers: getAuthHeaders(),
        });
        toast.success("Opportunity created");
      }
      setShowModal(false);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save opportunity");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try {
      await axios.delete(`${ADMIN_BASE_URL}/careers/opportunities/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Opportunity deleted");
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to delete opportunity");
    }
  };

  const openApplication = (application) => {
    setSelectedApplication(application);
    setPendingStatus(application.status);
    setShowApplicationModal(true);
  };

  const closeApplicationModal = () => {
    setShowApplicationModal(false);
    setSelectedApplication(null);
    setPendingStatus("");
  };

  const handleStatusSelect = (event) => {
    setPendingStatus(event.target.value);
  };

  const openStatusConfirmation = () => {
    if (!selectedApplication || !pendingStatus) return;
    if (pendingStatus === selectedApplication.status) {
      toast.error("Please choose a different status before updating.");
      return;
    }
    setShowStatusConfirmModal(true);
  };

  const confirmStatusUpdate = async () => {
    if (!selectedApplication || !pendingStatus) return;

    setUpdatingStatus(true);
    try {
      const response = await axios.patch(
        `${ADMIN_BASE_URL}/careers/applications/${selectedApplication._id}/status`,
        { status: pendingStatus, sendEmail: true },
        { headers: getAuthHeaders() }
      );
      setSelectedApplication(response.data);
      setShowStatusConfirmModal(false);
      toast.success("Application status updated and email sent");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (

    <div className="admin-faq-content">
      <div className="admin-faq-header">
        <h2>Careers</h2>
        {activeTab === "opportunities" && (
          <button type="button" className="admin-faq-btn" onClick={openCreate}>
            Add Opportunity
          </button>
        )}
      </div>

      <div className="mb-4 d-flex gap-2">
        <button
          type="button"
          className={`admin-faq-btn${activeTab === "opportunities" ? "" : " admin-faq-btn--secondary"}`}
          onClick={() => setActiveTab("opportunities")}
        >
          Opportunities
        </button>
        <button
          type="button"
          className={`admin-faq-btn${activeTab === "applications" ? "" : " admin-faq-btn--secondary"}`}
          onClick={() => setActiveTab("applications")}
        >
          Applications
        </button>
      </div>

      {loading ? (
        <p>Loading careers data...</p>
      ) : activeTab === "opportunities" ? (
        <div className="admin-faq-table-wrap">
          <table className="admin-faq-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Department</th>
                <th>Tag</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.length === 0 ? (
                <tr>
                  <td colSpan="5">No opportunities yet.</td>
                </tr>
              ) : (
                opportunities.map((opportunity) => (
                  <tr key={opportunity._id}>
                    <td>{opportunity.title}</td>
                    <td>{opportunity.department}</td>
                    <td>{opportunity.tag}</td>
                    <td>{opportunity.isActive ? "Yes" : "No"}</td>
                    <td>
                      <div className="admin-faq-actions">
                        <button
                          type="button"
                          className="admin-faq-btn"
                          onClick={() => openEdit(opportunity)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-faq-btn admin-faq-btn--danger"
                          onClick={() => handleDelete(opportunity._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-faq-table-wrap">
          <table className="admin-faq-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan="6">No applications yet.</td>
                </tr>
              ) : (
                applications.map((application) => (
                  <tr key={application._id}>
                    <td>{application.fullName}</td>
                    <td>{application.email}</td>
                    <td>{application.roleDepartment}</td>
                    <td>{application.status}</td>
                    <td>{new Date(application.createdAt).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-faq-btn"
                        onClick={() => openApplication(application)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        className="admin-careers-modal"
        backdropClassName="admin-careers-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Opportunity" : "Add Opportunity"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Select
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                required
              >
                {DEPARTMENTS.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tag</Form.Label>
              <Form.Control
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Meta</Form.Label>
              <Form.Control
                value={form.meta}
                onChange={(e) => setForm({ ...form, meta: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: Number(e.target.value) || 0 })
                }
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="opportunity-active"
              label="Active"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showApplicationModal}
        onHide={closeApplicationModal}
        centered
        size="lg"
        className="admin-careers-modal"
        backdropClassName="admin-careers-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <>
              <p><strong>Name:</strong> {selectedApplication.fullName}</p>
              <p><strong>Email:</strong> {selectedApplication.email}</p>
              <p><strong>Phone:</strong> {selectedApplication.phone}</p>
              <p><strong>Location:</strong> {selectedApplication.location}</p>
              <p><strong>LinkedIn:</strong> {selectedApplication.linkedinProfile || "—"}</p>
              <p><strong>Role:</strong> {selectedApplication.roleDepartment}</p>
              <p><strong>Opportunity:</strong> {selectedApplication.opportunityId?.title || "General application"}</p>
              <p><strong>Heard about us:</strong> {selectedApplication.hearAboutUs || "—"}</p>
              <p><strong>Work arrangement:</strong> {selectedApplication.workArrangement || "—"}</p>
              <p><strong>Availability:</strong> {selectedApplication.availability || "—"}</p>
              <p><strong>Current job:</strong> {selectedApplication.currentJobTitle || "—"}</p>
              <p><strong>Experience:</strong> {selectedApplication.yearsExperience || "—"}</p>
              <p><strong>Summary:</strong> {selectedApplication.experienceSummary || "—"}</p>
              <p><strong>Motivation:</strong> {selectedApplication.motivation || "—"}</p>
              <p><strong>Additional info:</strong> {selectedApplication.additionalInfo || "—"}</p>
              <p>
                <strong>CV:</strong>{" "}
                {selectedApplication.cvUrl ? (
                  <a href={selectedApplication.cvUrl} target="_blank" rel="noreferrer">
                    {selectedApplication.cvFileName || "View CV"}
                  </a>
                ) : (
                  "—"
                )}
              </p>
              <p><strong>Signature:</strong> {selectedApplication.signature}</p>
              <p><strong>Date:</strong> {selectedApplication.declarationDate}</p>
              <p><strong>Current status:</strong> {STATUS_LABELS[selectedApplication.status] || selectedApplication.status}</p>
              <Form.Group className="mt-3">
                <Form.Label>Update Status</Form.Label>
                <Form.Select value={pendingStatus} onChange={handleStatusSelect}>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeApplicationModal}>
            Close
          </Button>
          <Button variant="success" onClick={openStatusConfirmation}>
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStatusConfirmModal}
        onHide={() => setShowStatusConfirmModal(false)}
        centered
        className="admin-careers-modal"
        backdropClassName="admin-careers-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Status Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <>
              <p>
                You are about to update <strong>{selectedApplication.fullName}</strong>'s application
                from <strong>{STATUS_LABELS[selectedApplication.status]}</strong> to{" "}
                <strong>{STATUS_LABELS[pendingStatus]}</strong>.
              </p>
              <p className="mb-0">
                Confirming will send an update email to <strong>{selectedApplication.email}</strong> with the new application status.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmStatusUpdate} disabled={updatingStatus}>
            {updatingStatus ? "Sending..." : "Confirm & Send Email"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminCareers;
